import { useEffect, useRef, useState } from "react";
import DailyIframe from "@daily-co/daily-js";
import { Mic, MicOff, Video, VideoOff, PhoneOff } from "lucide-react";

// Full-screen 1:1 video/voice call overlay powered by Daily.co prebuilt UI.
// Receives a private room URL + a per-user meeting token from the backend.
export default function VideoCall({ roomUrl, token, onLeave }) {
  const containerRef = useRef(null);
  const frameRef = useRef(null);
  const [audioOn, setAudioOn] = useState(true);
  const [videoOn, setVideoOn] = useState(true);
  const [joining, setJoining] = useState(true);

  useEffect(() => {
    let frame = null;
    let cancelled = false;

    const setup = async () => {
      // daily-js keeps a module-level singleton; destroy() is async. Under
      // React.StrictMode the mount effect fires twice, so we MUST await any
      // existing instance's teardown before creating a new frame — otherwise
      // createFrame throws "Duplicate DailyIframe instances are not allowed".
      const existing = DailyIframe.getCallInstance();
      if (existing) { try { await existing.destroy(); } catch { /* noop */ } }
      if (cancelled || !containerRef.current) return;

      frame = DailyIframe.createFrame(containerRef.current, {
        iframeStyle: { width: "100%", height: "100%", border: "none", borderRadius: "16px" },
        showLeaveButton: false,
        showFullscreenButton: true,
      });
      frameRef.current = frame;
      frame.on("joined-meeting", () => setJoining(false));
      frame.on("left-meeting", () => { onLeave && onLeave(); });
      frame.on("error", () => { onLeave && onLeave(); });
      frame.join({ url: roomUrl, token });
    };

    setup();
    return () => {
      cancelled = true;
      if (frame) { try { frame.destroy(); } catch { /* noop */ } }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomUrl, token]);

  const toggleMic = () => {
    const next = !audioOn;
    frameRef.current?.setLocalAudio(next);
    setAudioOn(next);
  };
  const toggleVideo = () => {
    const next = !videoOn;
    frameRef.current?.setLocalVideo(next);
    setVideoOn(next);
  };
  const leave = () => { frameRef.current?.leave(); };

  return (
    <div className="fixed inset-0 z-[120] bg-black/95 backdrop-blur-sm flex flex-col p-3 sm:p-5" data-testid="video-call-overlay">
      <div ref={containerRef} className="flex-1 w-full bg-black rounded-2xl overflow-hidden" data-testid="video-call-frame" />
      {joining && (
        <p className="absolute top-6 left-1/2 -translate-x-1/2 text-zinc-400 text-sm font-mono-accent" data-testid="video-call-connecting">
          Connecting…
        </p>
      )}
      <div className="mt-4 flex justify-center items-center gap-4">
        <button onClick={toggleMic} data-testid="call-toggle-mic"
          className={`w-14 h-14 rounded-full flex items-center justify-center border transition-colors ${audioOn ? "bg-white/10 border-white/20 text-white" : "bg-[#E60000]/20 border-[#E60000]/60 text-[#FF6B6B]"}`}>
          {audioOn ? <Mic size={22} /> : <MicOff size={22} />}
        </button>
        <button onClick={leave} data-testid="call-hangup"
          className="w-16 h-16 rounded-full bg-[#E60000] hover:bg-[#FF1A1A] flex items-center justify-center text-white shadow-[0_0_30px_rgba(230,0,0,0.5)] transition-colors">
          <PhoneOff size={26} />
        </button>
        <button onClick={toggleVideo} data-testid="call-toggle-video"
          className={`w-14 h-14 rounded-full flex items-center justify-center border transition-colors ${videoOn ? "bg-white/10 border-white/20 text-white" : "bg-[#E60000]/20 border-[#E60000]/60 text-[#FF6B6B]"}`}>
          {videoOn ? <Video size={22} /> : <VideoOff size={22} />}
        </button>
      </div>
    </div>
  );
}
